/**
 * LegalDocModal — full-screen reader for a legal document (terms / privacy policy), opened from
 * the acceptance checkboxes at signup and the partner wizard. Renders the markdown-lite body
 * from src/constants/legal/* (## headings, - bullets, **bold**, paragraphs). Read-only: the
 * accept action stays on the form underneath, per clickwrap best practice (terms readable
 * BEFORE the acceptance click).
 */
import React from 'react';
import { X, ScrollText } from 'lucide-react';

/** Shape of a legal document constant (src/constants/legal/*, e.g. CONSUMER_TERMS, PRIVACY_POLICY). */
export interface LegalDoc {
  key: string;
  version: string;
  title: string;
  effectiveDate: string;
  draft: boolean;
  body: string;
}

/** Inline **bold** renderer. */
const Bold = ({ text }: { text: string }) => {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
};

function renderBody(body: string): React.ReactNode[] {
  const blocks: React.ReactNode[] = [];
  let list: string[] | null = null;
  const flushList = () => {
    if (list) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="my-3 ml-5 list-disc space-y-1.5">
          {list.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed text-gray-700">
              <Bold text={item} />
            </li>
          ))}
        </ul>,
      );
      list = null;
    }
  };

  // Paragraphs are separated by blank lines; single newlines inside a block are soft wraps.
  for (const raw of String(body).split(/\n\s*\n/)) {
    const block = raw.trim();
    if (!block) continue;
    if (block.startsWith('## ')) {
      flushList();
      blocks.push(
        <h3 key={`h-${blocks.length}`} className="mt-6 text-base font-bold text-slate-900">
          <Bold text={block.slice(3)} />
        </h3>,
      );
      continue;
    }
    const lines = block.split('\n').map((l) => l.trim());
    if (lines[0].startsWith('- ')) {
      // A bullet block: "- " starts an item; following lines without the marker are soft-wrapped
      // continuations of the current item.
      const items: string[] = [];
      for (const line of lines) {
        if (line.startsWith('- ')) items.push(line.slice(2));
        else if (items.length) items[items.length - 1] += ` ${line}`;
      }
      list = (list || ([] as string[])).concat(items);
      flushList();
      continue;
    }
    flushList();
    blocks.push(
      <p key={`p-${blocks.length}`} className="mt-3 text-sm leading-relaxed text-gray-700">
        <Bold text={lines.join(' ')} />
      </p>,
    );
  }
  flushList();
  return blocks;
}

interface LegalDocModalProps {
  doc: LegalDoc | null;
  onClose: () => void;
}

const LegalDocModal = ({ doc, onClose }: LegalDocModalProps) => {
  if (!doc) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 py-5 text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                <ScrollText size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold leading-tight">{doc.title}</h2>
                <p className="text-xs text-white/80">
                  Version {doc.version} · Effective {doc.effectiveDate}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-full p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        {doc.draft && (
          <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-2 text-xs font-medium text-amber-700">
            Draft for review — this document is being finalised with our legal advisers.
          </div>
        )}
        <div className="overflow-y-auto px-6 pb-8 pt-2">{renderBody(doc.body)}</div>
      </div>
    </div>
  );
};

export default LegalDocModal;
